import CurriculumConfirmationModal from "@/content-usage/ modals/CurriculumConfirmationModal"
import { ContentUsageMarkAsCompleteButtonFragment$key } from "@/content-usage/buttons/__generated__/ContentUsageMarkAsCompleteButtonFragment.graphql"
import useTrackContentCompletion from "@/content-usage/buttons/useTrackContentCompletion"
import { useActiveProduct } from "@/core/context/ActiveProductContext"
import { useLabel } from "@/core/context/LabelsContext"
import { DiscoButton, DiscoTooltip } from "@disco-ui"
import usePermissions from "@utils/hook/usePermissions"
import { TestIDProps } from "@utils/typeUtils"
import { useState } from "react"
import { useFragment } from "react-relay"
import { graphql } from "relay-runtime"

interface ContentUsageMarkAsCompleteButtonProps extends TestIDProps {
  contentUsageKey: ContentUsageMarkAsCompleteButtonFragment$key
}

function ContentUsageMarkAsCompleteButton({
  contentUsageKey,
  testid,
}: ContentUsageMarkAsCompleteButtonProps) {
  const activeProduct = useActiveProduct()!
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const isProductMember = Boolean(activeProduct.viewerMembership)
  const productLabel = useLabel("experience")

  const contentUsage = useFragment<ContentUsageMarkAsCompleteButtonFragment$key>(
    graphql`
      fragment ContentUsageMarkAsCompleteButtonFragment on ContentUsage {
        id
        ...useTrackContentCompletionFragment
        viewerScormCompletion {
          status
        }
        requireScormCompletion
        confirmationModalContent {
          ...CurriculumConfirmationModalConfirmationModalFragment
        }
        ...usePermissionsFragment
      }
    `,
    contentUsageKey
  )

  const canCompleteCurriculum = usePermissions(contentUsage).has("curriculum.complete")

  const viewerScormCompletionStatus = contentUsage.viewerScormCompletion?.status
  const isScormComplete =
    viewerScormCompletionStatus === "completed" ||
    viewerScormCompletionStatus === "passed"
  const isScormPending = contentUsage.requireScormCompletion && !isScormComplete

  const completeContent = useTrackContentCompletion(contentUsage)

  const isButtonDisabled = !canCompleteCurriculum || isLoading || isScormPending

  return (
    <>
      <DiscoTooltip content={getDisabledButtonTooltipContent()}>
        <DiscoButton
          testid={`${testid}.complete-item-button`}
          disabled={Boolean(isButtonDisabled)}
          shouldDisplaySpinner={isLoading}
          onClick={
            contentUsage.confirmationModalContent
              ? () => setIsOpen(true)
              : handleCompleteContent
          }
          rightIcon={"arrow-stem-right"}
        >
          {"Complete and Continue"}
        </DiscoButton>
      </DiscoTooltip>
      {contentUsage.confirmationModalContent && (
        <CurriculumConfirmationModal
          content={contentUsage.confirmationModalContent}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onConfirm={handleCompleteContent}
          isLoading={isLoading}
        />
      )}
    </>
  )

  function getDisabledButtonTooltipContent() {
    if (isScormPending) {
      return "You'll need to complete the learning activity before continuing. Please finish the embedded content first."
    }
    if (!canCompleteCurriculum) {
      return "This action cannot be taken while previewing."
    } else if (!isProductMember) {
      return `Must register for the ${productLabel.singular} to mark this as complete.`
    }
    return null
  }

  async function handleCompleteContent() {
    setIsLoading(true)
    await completeContent()
    setIsLoading(false)
  }
}
export default ContentUsageMarkAsCompleteButton
